import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import FormInputWrapper from '../form-input-wrapper/FormInputWrapper.jsx';

import {
  addValidationField,
  removeValidationField,
  addDependentsToIgnoreList,
  removeDependentsFromIgnoreList,
  addPreventFormSubmissionField,
  removePreventFormSubmissionField
} from '../redux/form/form.actions';

import { uuidv4, filterArrayByProperty } from '../_global/js/form.utils';

const FormRadio = ({
  groupId,
  inputLabel,
  inputDescriptionText,
  parentElementClass,
  inputName,
  inputRequired,
  inputOptions,
  sectionLabel,
  inputValidationLabel,
  inputValidationMessage,
  conditionalInputs,
  dependentInputs,
  additionalInputs,
  preventProgression,
  inputTestData,
  autoPopulate,
  triggerValidation,
  formStepValidation,
  addValidationField,
  removeValidationField,
  addDependentsToIgnoreList,
  removeDependentsFromIgnoreList,
  addPreventFormSubmissionField,
  removePreventFormSubmissionField
  }) => {
  const inputId = useRef([]);
  const [ inputValue, setInputValue ] = useState('');
  const [ isValid, setIsValid ] = useState(true);
  const [ enableAdditional, setEnableAdditional ] = useState(false);
  const [ showAdditional, setShowAdditional ] = useState(false);
  const [ conditionalQuestions, setConditionalQuestions ] = useState(null);
  const [ requiresFormValidation, setRequiresFormValidation ] = useState(false);
  const validationLabel = inputValidationLabel || inputLabel;
  const errorMessage = inputValidationMessage || 'This field is required.';

  // Create a unique ID for each input in the radio group
  if (inputId.current.length === 0) {
    for (let i = 0, j = inputOptions.length; i < j; ++i) {
      inputId.current.push( uuidv4() );
    }
  }

  /**
   * When the component has been mounted
   */
  useEffect(() => {
    let nonMatchingDependentFieldReferencesArray = [];

    // Mark this field as required, as necessary
    if (inputRequired) {
      setRequiresFormValidation(true);
    }

    // Check if there are additional inputs to show
    if (additionalInputs !== undefined && additionalInputs !== null) {
      if (additionalInputs.responseMatch === null || additionalInputs.responseMatch === undefined) {
        // Unconditionally show the additional questions
        setEnableAdditional(true);
      }
    }

    // Automatically hide any dependent inputs
    if (dependentInputs !== undefined && dependentInputs !== null) {
      // Capture all the input names
      for (let i = 0, j = dependentInputs.length; i < j; ++i) {
        nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(dependentInputs[i].inputs);
      }

      addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
    }

    // Cleanup the component (unmount)
    return () => {
      // Remove this field from the invalid list
      removeValidationField(inputId.current[0]);

      // Remove this field from the list preventing the form submission (if applicable)
      removePreventFormSubmissionField(inputId.current[0]);

      // Reset any dependent questions
      // resetDependentQuestions(dependentInputs);
    };
  }, []);

  /**
   * When the field validation requirement has changed
   */
  useEffect(() => {
    if (requiresFormValidation) {
      // Add this field to the form validation state
      addValidationField({
        id: inputId.current[0],
        label: validationLabel,
        sectionLabel: sectionLabel,
        required: inputRequired,
        formStep: formStepValidation
      });
    } else {
      // Remove this field from the form validation state
      removeValidationField(inputId.current[0]);
    }
  }, [requiresFormValidation]);

  /**
   * When the component has been instructed to validate it's input
   */
  useEffect(() => {
    if (triggerValidation !== null) {

      // Check if this input is in the current form step
      if (triggerValidation.formStep === formStepValidation) {
        // Check if the input is valid
        const validationSuccess = inputIsValid(inputValue);

        // Field is required, but failed validation
        if (inputRequired && !validationSuccess) {
          // Display the error message
          setIsValid(false);

          // Mark this field as invalid
          setRequiresFormValidation(true);
        }
      }
    }
  }, [triggerValidation]);

  /**
   * When the component has been instructed to auto-populate content
   */
  useEffect(() => {
    // Populate the field with test data
    if (autoPopulate && inputTestData) {
      setInputValue(inputTestData);
      setIsValid(true);
      setRequiresFormValidation(false);
    }
  }, [autoPopulate]);

  /**
   * When the value of the input has changed
   */
  useEffect(() => {
    let conditionalFieldsArray = null;
    let conditionalFieldsData = null;
    let additionalInputsRegex = null;
    let matchingDependentFieldsArray = null;
    let nonMatchingDependentFieldsArray = null;
    let nonMatchingDependentFieldReferencesArray = [];
    let preventFormSubmission = false;

    /**
     * Check if the user has entered a value and if there are
     * further actions to take. e.g. additional fields or checks.
     */
    if (inputIsValid(inputValue)) {

      if (conditionalInputs) {
        // If conditional inputs have been provided, check for any matches
        conditionalFieldsArray = filterArrayByProperty(conditionalInputs, 'responseMatch', inputValue);

        if (conditionalFieldsArray.length !== 0) {
          // Assign an ID to each input
          conditionalFieldsData = conditionalFieldsArray[0].inputs;

          for (let i = 0, j = conditionalFieldsData.length; i < j; ++i) {
            conditionalFieldsData[i].id = uuidv4();
          }

          setConditionalQuestions(conditionalFieldsData);
        } else {
          setConditionalQuestions(null);
        }
      }

      // Check if the additional inputs should be displayed
      if (additionalInputs !== undefined && additionalInputs !== null) {

        if (additionalInputs.responseMatch !== null && additionalInputs.responseMatch !== undefined) {
          // Test if the response matches the condition for the additional inputs
          additionalInputsRegex = new RegExp(additionalInputs.responseMatch, 'g');

          if (additionalInputsRegex.test(inputValue)) {
            setEnableAdditional(true);
          } else {
            setEnableAdditional(false);
            setShowAdditional(false);
          }
        }
      }

      if (dependentInputs !== undefined && dependentInputs !== null) {
        // If dependent inputs have been provided, update the ignore list as necessary
        matchingDependentFieldsArray = filterArrayByProperty(dependentInputs, 'responseMatch', inputValue);
        nonMatchingDependentFieldsArray = filterArrayByProperty(dependentInputs, 'responseMatch', inputValue, 0, true);

        /**
         * Note that we hide fields BEFORE showing any.
         * This order is important so that the same fields can appear in
         * different lists of dependent fields.
         * That is, if the same input appears in multiple responseMatch
         * checks, we want to make sure it's still visible after the
         * update to the state.
         */
        if (nonMatchingDependentFieldsArray.length !== 0) {
          // One or more dependents should be hidden
          // Capture all the input names
          for (let i = 0, j = nonMatchingDependentFieldsArray.length; i < j; ++i) {
            nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(nonMatchingDependentFieldsArray[i].inputs);
          }

          addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
        }

        if (matchingDependentFieldsArray.length !== 0) {
          // One or more dependents can be shown
          removeDependentsFromIgnoreList(matchingDependentFieldsArray[0].inputs);
        }
      }

      // Check if the response should prevent the form progress and submission
      if (preventProgression !== undefined && preventProgression !== null) {
        // Check for matching values
        for (let i = 0, j = preventProgression.length; i < j; ++i) {
          if (inputValue === preventProgression[i]) {
            // A match was found
            preventFormSubmission = true;
            break;
          }
        }

        if (preventFormSubmission) {
          // Prevent the form submission
          addPreventFormSubmissionField(inputId.current[0]);
        } else {
          // Re-enable form submission
          removePreventFormSubmissionField(inputId.current[0]);
        }
      }
    } else {
      // Reset any conditional questions
      if (conditionalQuestions !== null) {
        setConditionalQuestions(null);
      }

      // Reset any dependent questions
      resetDependentQuestions(dependentInputs);
    }
  }, [inputValue]);

  const onInputChange = (evt) => {
    const fieldValue = evt.target.value;

    // Check if the input is valid
    const validationSuccess = inputIsValid(fieldValue);

    // Capture the inputted value
    setInputValue(fieldValue);

    // Field is required, but failed validation
    if (inputRequired && !validationSuccess) {
      // Display the error message
      setIsValid(false);

      // Mark this field as invalid
      setRequiresFormValidation(true);
    }

    // Field passed validation
    if (validationSuccess) {
      // Update the validation message
      setIsValid(true);

      // Remove this field from the invalid list
      setRequiresFormValidation(false);
    }
  };

  const onAdditionalButtonClick = (evt) => {
    evt.preventDefault();

    if (!showAdditional) {
      setShowAdditional(true);
    } else {
      setShowAdditional(false);
    }
  };

  /**
   * Validate the field's value
   *
   * @param {String} valueToCheck The value of the input field
   *
   * @returns Boolean
   */
  function inputIsValid(valueToCheck) {
    let isValid = false;

    if (valueToCheck !== '') {
      isValid = true;
    }

    return isValid;
  }

  /**
   * Capture the input names of all questions that are
   * dependnent on the response of this question and
   * add them global ignore list
   *
   * @param {Array} dependentInputsConfiguration The entire dependent questions configuration
   *
   * @returns void
   */
  function resetDependentQuestions(dependentInputsConfiguration) {
    let nonMatchingDependentFieldReferencesArray = [];

    if (dependentInputsConfiguration !== undefined && dependentInputsConfiguration !== null) {
      // Loop over the possible dependent questions
      for (let k = 0, b = dependentInputsConfiguration.length; k < b; ++k) {
        // Capture all dependent inputs
        nonMatchingDependentFieldReferencesArray = nonMatchingDependentFieldReferencesArray.concat(dependentInputsConfiguration[k].inputs);
      }

      addDependentsToIgnoreList(nonMatchingDependentFieldReferencesArray);
    }
  }

  /**
   * Note that the name value is actually an ID.
   *
   * This is because radio inputs with the same name
   * value are all treated as part of the same group
   *  - regardless of where they appear in the form.
   *
   * To solve this issue, and allow the name attribute
   * to be re-used in the form, we have a "clean name"
   * attribute which we process for radio buttons in
   * the form submission methods.
   */
  return (
    <div className={parentElementClass}>
      <div>
        <fieldset>
          <legend className="qld__label">{ (inputRequired) ? <abbr title="required">*</abbr> : null } {inputLabel}</legend>
          {
            (inputDescriptionText !== undefined && inputDescriptionText !== null) ? <span id={`${inputId.current}-description`} className="qld__hint-text">{inputDescriptionText}</span> : null
          }
          {
            (!isValid) ?
              <p className="sq-form-error">
                {errorMessage}
              </p>
            : null
          }
          <ul className="custom-online-form__response-list">
            {
              inputOptions.map( (radio, index) => {
                return (
                  <li key={inputId.current[index]}>
                    <input
                      id={inputId.current[index]}
                      className={!isValid ? "qld__input--error" : null}
                      name={inputId.current[0]}
                      type="radio"
                      value={radio.value}
                      onChange={onInputChange}
                      checked={ (radio.value === inputValue) ? true : false }
                      autoComplete="off"
                      data-groupid={groupId}
                      data-validation-label={validationLabel}
                      data-section-label={sectionLabel}
                      data-clean-name={inputName}
                    />
                    <label htmlFor={inputId.current[index]}>{radio.label}</label>
                  </li>
                  );
              })
            }
          </ul>
        </fieldset>
      </div>
      {
        (conditionalQuestions) ?
          <div>
            {
              conditionalQuestions.map( (formInput) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={formInputConfig.id}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
      {
        (enableAdditional && !showAdditional) ? <button onClick={onAdditionalButtonClick} className="qld__btn">{additionalInputs.buttonLabel}</button> : null
      }
      {
        (enableAdditional && showAdditional) ?
          <div>
            <div>
              <button onClick={onAdditionalButtonClick} className="qld__btn qld__btn--secondary">Remove this section</button>
            </div>
            {
              additionalInputs.inputs.map( (formInput, index) => {
                const formInputConfig = { ...formInput };

                /**
                 * If provided, add the section legend (label) to the input configuration.
                 * This is so we can include the section label in the form validation messages and summary.
                 */
                if (sectionLabel) {
                  formInputConfig.sectionLabel = sectionLabel;
                }

                return (
                  <FormInputWrapper
                    key={index}
                    inputConfiguration={formInputConfig}
                    autoPopulate={autoPopulate}
                    triggerValidation={triggerValidation}
                    formStepValidation={formStepValidation}
                  />
                );
              })
            }
          </div>
        : null
      }
    </div>
  );
};

FormRadio.propTypes = {
  "groupId": PropTypes.string,
  "inputLabel": PropTypes.string.isRequired,
  "inputDescriptionText": PropTypes.string,
  "parentElementClass": PropTypes.string,
  "inputName": PropTypes.string.isRequired,
  "inputRequired": PropTypes.bool.isRequired,
  "inputOptions": PropTypes.array.isRequired,
  "sectionLabel": PropTypes.string,
  "inputValidationLabel": PropTypes.string,
  "inputValidationMessage": PropTypes.string,
  "conditionalInputs": PropTypes.array,
  "dependentInputs": PropTypes.array,
  "additionalInputs": PropTypes.object,
  "preventProgression": PropTypes.array,
  "inputTestData": PropTypes.string,
  "autoPopulate": PropTypes.bool,
  "triggerValidation": PropTypes.object,
  "formStepValidation": PropTypes.number.isRequired,
  "addValidationField":  PropTypes.oneOfType( [PropTypes.object, PropTypes.func] ),
  "removeValidationField":  PropTypes.oneOfType( [PropTypes.string, PropTypes.func] ),
  "addDependentsToIgnoreList":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "removeDependentsFromIgnoreList":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "addPreventFormSubmissionField":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] ),
  "removePreventFormSubmissionField":  PropTypes.oneOfType( [PropTypes.array, PropTypes.func] )
};

const mapDispatchToProps = (dispatch) => {
  return {
    "addValidationField": formFieldData => dispatch(addValidationField(formFieldData)),
    "removeValidationField": formFieldData => dispatch(removeValidationField(formFieldData)),
    "addDependentsToIgnoreList": inputReferences => dispatch(addDependentsToIgnoreList(inputReferences)),
    "removeDependentsFromIgnoreList": inputReferences => dispatch(removeDependentsFromIgnoreList(inputReferences)),
    "addPreventFormSubmissionField": inputNames => dispatch(addPreventFormSubmissionField(inputNames)),
    "removePreventFormSubmissionField": inputNames => dispatch(removePreventFormSubmissionField(inputNames))
  };
};

export default connect(null, mapDispatchToProps)(FormRadio);